/**
 * i18n — Business app localization.
 *
 * Authority: UX Blueprint §9 (UX Invariant — no hardcoded user-facing strings).
 *
 * Namespaces:
 *   - common: shared UI (buttons, labels, nav)
 *   - auth: login / tenant switcher
 *   - layout: sidebar, topbar, breadcrumbs
 *   - appointments: B1.x screens (calendar, side sheet, cashier view)
 *   - customers: B2.x screens
 *   - inventory: B3.x screens
 *
 * Sub-vertical terminology override (ADR-100): BusinessProfile.terminology
 * can replace any key via `t(key, { context: businessProfileKey })`.
 */
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { APP_ENV } from '../config/env';
import trCommon from './locales/tr/common.json';
import trAuth from './locales/tr/auth.json';
import trLayout from './locales/tr/layout.json';
import trSettings from './locales/tr/settings.json';
import trAdminProfiles from './locales/tr/adminProfiles.json';
import trAdminMigrations from './locales/tr/adminMigrations.json';
import trAdminPolicies from './locales/tr/adminPolicies.json';
import trCalendar from './locales/tr/calendar.json';
import trAppointmentForm from './locales/tr/appointmentForm.json';
import trTimeline from './locales/tr/timeline.json';
import trCustomers from './locales/tr/customers.json';
import trOnboarding from './locales/tr/onboarding.json';
import trDashboard from './locales/tr/dashboard.json';
import trCashier from './locales/tr/cashier.json';
import trPayment from './locales/tr/payment.json';
import trDayClose from './locales/tr/dayClose.json';
import trInvoices from './locales/tr/invoices.json';
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enLayout from './locales/en/layout.json';
import enSettings from './locales/en/settings.json';
import enAdminProfiles from './locales/en/adminProfiles.json';
import enAdminMigrations from './locales/en/adminMigrations.json';
import enAdminPolicies from './locales/en/adminPolicies.json';
import enCalendar from './locales/en/calendar.json';
import enAppointmentForm from './locales/en/appointmentForm.json';
import enTimeline from './locales/en/timeline.json';
import enCustomers from './locales/en/customers.json';
import enOnboarding from './locales/en/onboarding.json';
import enDashboard from './locales/en/dashboard.json';
import enCashier from './locales/en/cashier.json';
import enPayment from './locales/en/payment.json';
import enDayClose from './locales/en/dayClose.json';
import enInvoices from './locales/en/invoices.json';

export const SUPPORTED_LOCALES = APP_ENV.availableLocales;

export async function initI18n(): Promise<typeof i18next> {
  if (i18next.isInitialized) return i18next;

  await i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        tr: {
          common: trCommon,
          auth: trAuth,
          layout: trLayout,
          settings: trSettings,
          adminProfiles: trAdminProfiles,
          adminMigrations: trAdminMigrations,
          adminPolicies: trAdminPolicies,
          calendar: trCalendar,
          appointmentForm: trAppointmentForm,
          timeline: trTimeline,
          customers: trCustomers,
          onboarding: trOnboarding,
          dashboard: trDashboard,
          cashier: trCashier,
          payment: trPayment,
          dayClose: trDayClose,
          invoices: trInvoices,
        },
        en: {
          common: enCommon,
          auth: enAuth,
          layout: enLayout,
          settings: enSettings,
          adminProfiles: enAdminProfiles,
          adminMigrations: enAdminMigrations,
          adminPolicies: enAdminPolicies,
          calendar: enCalendar,
          appointmentForm: enAppointmentForm,
          timeline: enTimeline,
          customers: enCustomers,
          onboarding: enOnboarding,
          dashboard: enDashboard,
          cashier: enCashier,
          payment: enPayment,
          dayClose: enDayClose,
          invoices: enInvoices,
        },
      },
      fallbackLng: APP_ENV.defaultLocale,
      supportedLngs: SUPPORTED_LOCALES,
      defaultNS: 'common',
      ns: [
        'common',
        'auth',
        'layout',
        'settings',
        'adminProfiles',
        'adminMigrations',
        'adminPolicies',
        'calendar',
        'appointmentForm',
        'timeline',
        'customers',
        'onboarding',
        'dashboard',
        'cashier',
        'payment',
        'dayClose',
        'invoices',
      ],
      interpolation: {
        escapeValue: false,
      },
      detection: {
        order: ['localStorage', 'navigator', 'htmlTag'],
        caches: ['localStorage'],
        lookupLocalStorage: 'petota-business:locale',
      },
      react: {
        useSuspense: false,
      },
    });

  return i18next;
}

export default i18next;
